import { useEffect, useMemo, useRef, useState } from 'react';

import { Eye, Monitor, Smartphone } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';

interface EmailPreviewProps {
  html: string;
  variables: Record<string, string>;
  subject?: string;
  triggerLabel?: string;
  className?: string;
}

const SAMPLE_VALUES: Record<string, string> = {
  // Flat template variable names (for mapped templates)
  customer_name: 'John Doe',
  phone_number: '+44 7700 900000',
  company_name: 'Acme Corp',
  company_logo: '',
  campaign_name: 'Q1 Outreach',
  vehicle_interest: '2024 BMW 3 Series',
  finance_preference: 'PCP Finance',
  additional_notes: 'Customer expressed strong interest and wants to visit this weekend.',
  callback_reason: 'Wanted to discuss trade-in options',
  customer_question: 'What are the finance rates for used vehicles?',
  preferred_callback_time: 'Tomorrow afternoon',
  dashboard_url: 'https://app.example.com',
  call_id: 'call-abc-123',
  agent_name: 'Alex (AI Agent)',
  call_ended_at: new Date().toLocaleString(),
  availability_slots: 'Monday 9am-5pm, Wednesday 2pm-8pm',
  availability_slots_count: '2',
  availability_notes: 'Prefers morning shifts.',
  shift_notes: 'Confirmed for next week.',
  qualification_label: 'Fully Qualified',
  should_send_callback_email: 'true',

  // Direct context paths (for templates using paths directly)
  'call.id': 'call-abc-123',
  'call.direction': 'INBOUND',
  'call.channel': 'voice',
  'call.modality': 'VOICE',
  'call.outcome': 'LEAD_CAPTURED',
  'call.duration': '120',
  'call.phone_number': '+44 7700 900000',
  'contact.name': 'John Doe',
  'contact.phone': '+44 7700 900000',
  'campaign.name': 'Q1 Outreach',
  'campaign.id': 'camp-123',
  'config.notification_emails': 'admin@example.com',
  'review.reviewed_outcome': 'LEAD_CAPTURED',
  'review.ai_analysis': 'Customer showed strong buying intent.',
  'review.confidence_score': '0.92',
  'review.lead_data.customer_name': 'John Doe',
  'review.lead_data.vehicle_interest': '2024 BMW 3 Series',
  'review.lead_data.budget': '25,000',
  'review.lead_data.finance_preference': 'PCP Finance',
  'review.lead_data.additional_notes': 'Customer expressed strong interest.',
  'review.lead_data.customer_status': 'still_looking',
  'review.lead_data.showroom_interest': 'true',
  'review.lead_data.selected_vehicle.vdp_url': 'https://example.com/car',
  'review.lead_data.selected_vehicle.registration': 'AB12 CDE',
  'review.reviewed_lead_data.phone': '+44 7700 900000',
  'review.reviewed_lead_data.budget': '25,000',
  'review.reviewed_lead_data.finance_preference': 'PCP Finance',
  'review.reviewed_lead_data.vehicle_interest': '2024 BMW 3 Series',
  'review.additional_review_data.should_send_callback_email': 'true',
  'review.additional_review_data.callback_reason': 'Wanted to discuss trade-in options',
  'review.callback_data.customer_question': 'What are the finance rates?',
  'review.callback_data.preferred_callback_time': 'Tomorrow afternoon',
  'review.enriched_vehicle.name': '2024 BMW 3 Series',
  'review.enriched_vehicle.registration': 'AB12 CDE',
  'review.enriched_vehicle.checkout_url': 'https://example.com/reserve',
  'review.showroom_time.date': '25/04/2026 at 10:00',
  sms_sent: 'true',
  sms_not_sent: '',
};

function resolvePreviewHtml(html: string, variables: Record<string, string>): string {
  // Process {{#if var}}...{{#else}}...{{/if}} blocks
  let result = html.replace(
    /\{\{#if\s+([^}]+)\}\}([\s\S]*?)(?:\{\{#else\}\}([\s\S]*?))?\{\{\/if\}\}/g,
    (_match, path: string, ifContent: string, elseContent?: string) => {
      const value = variables[path.trim()];
      return value && value !== 'false' && value !== '' ? ifContent : (elseContent ?? '');
    },
  );

  // Replace {{var}} tokens
  result = result.replace(/\{\{([^}]+)\}\}/g, (_match, varName: string) => {
    const name = varName.trim();
    if (variables[name] !== undefined && variables[name] !== '') {
      return variables[name];
    }
    return `<span style="background:#e5e7eb;color:#6b7280;padding:1px 6px;border-radius:4px;font-size:12px;font-family:monospace;">[${name}]</span>`;
  });

  return result;
}

export const EmailPreview = ({
  html,
  variables,
  subject,
  triggerLabel = 'Preview',
  className,
}: EmailPreviewProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [width, setWidth] = useState<'desktop' | 'mobile'>('desktop');
  const [editableVars, setEditableVars] = useState<Record<string, string>>({});
  const [showVarEditor, setShowVarEditor] = useState(false);

  // Merge: provided variables > editable overrides > sample defaults
  const mergedVars = useMemo(() => {
    const merged: Record<string, string> = { ...SAMPLE_VALUES };
    for (const [key, value] of Object.entries(variables)) {
      if (value) merged[key] = value;
    }
    for (const [key, value] of Object.entries(editableVars)) {
      if (value !== undefined) merged[key] = value;
    }
    return merged;
  }, [variables, editableVars]);

  const resolvedHtml = useMemo(() => resolvePreviewHtml(html, mergedVars), [html, mergedVars]);
  const resolvedSubject = useMemo(
    () =>
      subject?.replace(
        /\{\{([^}]+)\}\}/g,
        (_m, v: string) => mergedVars[v.trim()] ?? `[${v.trim()}]`,
      ) ?? '',
    [subject, mergedVars],
  );

  // Extract variable names from the template for the editor
  const templateVarNames = useMemo(() => {
    const matches = html.match(/\{\{([^}]+)\}\}/g) || [];
    return [
      ...new Set(
        matches
          .map((m) => m.replace(/\{\{|\}\}/g, '').trim())
          .filter((v) => !v.startsWith('#if ') && v !== '/if'),
      ),
    ];
  }, [html]);

  // Extract {{#if varName}} condition names for toggle switches.
  // Only show toggles for PURE conditionals — skip variables that also appear
  // as {{variable}} (those are inline badges controlled by the text input).
  const conditionalVarNames = useMemo(() => {
    const ifMatches = html.match(/\{\{#if\s+([^}]+)\}\}/g) || [];
    const allIfVars = [...new Set(ifMatches.map((m) => m.replace(/\{\{#if\s+|\}\}/g, '').trim()))];
    return allIfVars.filter((v) => !templateVarNames.includes(v));
  }, [html, templateVarNames]);

  useEffect(() => {
    if (iframeRef.current) {
      const doc = iframeRef.current.contentDocument;
      if (doc) {
        doc.open();
        doc.write(resolvedHtml);
        doc.close();
      }
    }
  }, [resolvedHtml]);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className={className}>
          <Eye size={14} className="mr-1" />
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="flex h-[85vh] max-w-4xl flex-col">
        <DialogHeader>
          <DialogTitle>Email Preview</DialogTitle>
        </DialogHeader>

        {/* Toolbar */}
        <div className="flex items-center justify-between border-b pb-2">
          <div className="flex gap-1">
            <Button
              size="sm"
              variant={width === 'desktop' ? 'default' : 'outline'}
              onClick={() => setWidth('desktop')}
            >
              <Monitor size={14} className="mr-1" />
              Desktop
            </Button>
            <Button
              size="sm"
              variant={width === 'mobile' ? 'default' : 'outline'}
              onClick={() => setWidth('mobile')}
            >
              <Smartphone size={14} className="mr-1" />
              Mobile
            </Button>
          </div>
          <Button size="sm" variant="outline" onClick={() => setShowVarEditor(!showVarEditor)}>
            {showVarEditor ? 'Hide' : 'Edit'} Variables
          </Button>
        </div>

        {/* Subject preview */}
        {resolvedSubject && (
          <div className="rounded-md border px-3 py-2 text-sm">
            <span className="text-muted-foreground mr-2 text-xs font-medium">Subject:</span>
            {resolvedSubject}
          </div>
        )}

        <div className="flex flex-1 gap-4 overflow-hidden">
          {/* Variable editor panel */}
          {showVarEditor && (
            <div className="w-64 shrink-0 space-y-2 overflow-y-auto border-r pr-4">
              {conditionalVarNames.length > 0 && (
                <>
                  <p className="text-muted-foreground text-xs font-medium">Conditional Sections</p>
                  {conditionalVarNames.map((name) => (
                    <div key={`toggle-${name}`} className="flex items-center justify-between gap-2">
                      <label className="text-muted-foreground truncate font-mono text-[10px]">
                        {name}
                      </label>
                      <Switch
                        checked={
                          (editableVars[name] ?? mergedVars[name] ?? 'true') !== '' &&
                          (editableVars[name] ?? mergedVars[name] ?? 'true') !== 'false'
                        }
                        onCheckedChange={(checked) =>
                          setEditableVars((prev) => ({
                            ...prev,
                            [name]: checked ? 'true' : 'false',
                          }))
                        }
                      />
                    </div>
                  ))}
                  <div className="border-b pb-1" />
                </>
              )}
              <p className="text-muted-foreground text-xs font-medium">Sample Values</p>
              {templateVarNames.map((name) => (
                <div key={name} className="space-y-0.5">
                  <label className="text-muted-foreground truncate font-mono text-[10px]">
                    {name}
                  </label>
                  <Input
                    value={editableVars[name] ?? mergedVars[name] ?? ''}
                    onChange={(e) =>
                      setEditableVars((prev) => ({ ...prev, [name]: e.target.value }))
                    }
                    className="h-7 text-xs"
                  />
                </div>
              ))}
            </div>
          )}

          {/* Email iframe */}
          <div className="flex flex-1 justify-center overflow-auto bg-gray-100 p-4">
            <iframe
              ref={iframeRef}
              title="Email Preview"
              className="border bg-white shadow-sm"
              style={{
                width: width === 'desktop' ? 650 : 375,
                height: '100%',
                minHeight: 500,
              }}
              sandbox="allow-same-origin"
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
